/*
 * File: ResourcePrompt.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 11, 2025 at 4:10 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

// Utilities
import { buildDynamicMeta } from '../../../components/Meta';
import { scrollPageToHash } from '../../../utilities/position';
import { generateShortTitlePlaceholder } from '../../../utilities/placeholder';

// Services
import { fetchResourcePrompt } from '../../../services/resource';
import { recordView } from '../../../services/view';

// Store
import { updateCurrentResourcePrompt, updateCurrentResourcePromptError } from '../../../store/slices/currentResourcePrompt/currentResourcePrompt.slice';
import { updateCountryId } from '../../../store/slices/locale/locale.slice';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	Typography,
	ErrorComponent,
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';

// Styles
import * as S from './ResourcePrompt.styles';


/**
 * Component
 */

const ResourcePrompt = ({ meta: m }) => {

	// Get component parameters
	const { slug } = useParams();

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current resource prompt from hook
	let { status: resourcePromptStatus, value: resourcePrompt } = useSelector((state) => state.currentResourcePrompt);
	if (resourcePrompt?.slug && resourcePrompt?.slug !== slug) {
		resourcePromptStatus = 'initial';
		resourcePrompt = null;
	}

	// Check if loading
	const isLoading = useMemo(() => resourcePromptStatus !== 'final', [resourcePromptStatus]);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Generate placeholders
	const promptPlaceholder = useMemo(() => generateShortTitlePlaceholder(), []);

	// Use hooks
	const dispatch = useDispatch();
	const location = useLocation();

	// Get location hash
	const { hash } = location;

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageResourcePrompt.metaTitle', { partnerName: partner.name, resourcePromptName: resourcePrompt ? resourcePrompt?.prompt_locales[locale.localeBaseId] : t('resourcesName') }),
		description: resourcePrompt?.explanation_locales[locale.localeBaseId],
		bodyClasses: 'hide-captcha',
		...m || {},
		...resourcePrompt && resourcePrompt.allow_indexing === false ? {
			shouldIndex: false
		} : undefined
	}), [m, resourcePrompt, partner, locale, t]);

	// Define parent meta content
	const parentMeta = useMemo(() => ({
		title: t('pageSupport.metaTitle', { partnerName: partner.name })
	}), [partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Handle fetch current resource prompt
	const fetchCurrentResourcePrompt = async () => {
		try {

			// Fetch resource prompt
			const { data: { result: { resource_prompt: prompt, required_redirect: requiredRedirect } } } = await fetchResourcePrompt({ slug });

			// Handle redirect if necessary
			if (requiredRedirect) {

				// Get redirect parameters
				const { country_id: countryId } = requiredRedirect;

				// Update country id
				dispatch(updateCountryId({
					countryId,
					partner
				}));
			}

			// Update resource prompt
			dispatch(updateCurrentResourcePrompt(prompt));

		} catch (e) {

			// Update state
			dispatch(updateCurrentResourcePromptError(e.message));
		}
	};

	// Handle actions on status update
	useEffect(() => {
		if (resourcePromptStatus === 'final') {

			// Scroll to hash
			scrollPageToHash(hash);
		}
	}, [resourcePromptStatus, hash]);

	// Handle actions on status update
	useEffect(() => {
		if (resourcePromptStatus === 'final') {

			// Record view
			recordView({
				source: SOURCES.RESOURCE_PROMPT,
				entityId: 'TODO',
				urlPath: window.location.pathname
			});
		}
	}, [resourcePromptStatus]);

	// Handle actions on component load
	useEffect(() => {

		// Check if should load
		if (resourcePromptStatus !== 'final') {

			// Fetch current resource prompt
			fetchCurrentResourcePrompt();
		}

	}, [slug]);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/resources/p/${resourcePrompt?.slug}/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/resources/#ListItem`,
							name: buildDynamicMeta(parentMeta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/resources`
						}
					},
					{
						'@type': 'ListItem',
						position: 2,
						item: {
							'@id': `${partnerDomain}/resources/p/${resourcePrompt?.slug}/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/resources/p/${resourcePrompt?.slug}`
						}
					}
				]
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{resourcePromptStatus !== 'error' ? (
						<div>

							{/* Title */}
							<Typography
								tag="h1"
								weight="black"
								className={isLoading && 'shimmer shimmer-element'}
							>
								{resourcePrompt?.prompt_locales[locale.localeBaseId] || promptPlaceholder}
							</Typography>

							{/* NV - COMPLETE */}

						</div>
					) : <ErrorComponent showMeta />}

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

ResourcePrompt.propTypes = {
	meta: PropTypes.shape()
};
ResourcePrompt.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default ResourcePrompt;
